import React, { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import logo from '../kloudynet-logo.jpeg';
import SignInButton from '../auth/SignInButton';
import { setActiveTenantId } from '../auth/msalConfig';
import { useCurrentUser } from '../auth/useCurrentUser';

const API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:5295';

export default function ActivatePage({ token }) {
  const isAuthenticated = useIsAuthenticated();
  const { email, refresh } = useCurrentUser();

  const [phase, setPhase] = useState('idle'); // idle | resolving | resolved | activating | done | error | mismatch
  const [resolved, setResolved] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [expectedEmail, setExpectedEmail] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !token || phase !== 'idle') return;
    (async () => {
      setPhase('resolving');
      try {
        const res = await fetch(`${API}/api/v1/marketplace/resolve?token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Resolve failed: ${res.status} ${text}`);
        }
        const body = await res.json();
        setResolved(body);
        setPhase('resolved');
      } catch (e) {
        setErrorDetail(e.message);
        setPhase('error');
      }
    })();
  }, [isAuthenticated, token, phase]);

  const confirmActivation = async () => {
    setPhase('activating');
    setErrorDetail(null);
    try {
      const res = await fetch(`${API}/api/v1/marketplace/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ landingPageToken: token }),
      });
      if (res.status === 403) {
        // Could be either a generic forbidden OR the purchaser-mismatch we
        // care about. Look for an email in the body to disambiguate.
        const text = await res.text();
        const match = text.match(/Marketplace purchaser '([^']+)'/);
        if (match) {
          setExpectedEmail(match[1]);
          setPhase('mismatch');
          return;
        }
        throw new Error(`Activate refused: ${text}`);
      }
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Activate failed: ${res.status} ${text}`);
      }
      const body = await res.json();
      setActiveTenantId(body.tenantId);
      await refresh();
      setPhase('done');
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (e) {
      setErrorDetail(e.message);
      setPhase('error');
    }
  };

  if (!token) {
    return <Wrap title="Invalid activation link" detail="The link is missing a Marketplace token." />;
  }

  if (!isAuthenticated) {
    return (
      <Wrap title="Activate your SAARA subscription"
        detail="Sign in with the same email used at purchase to complete activation.">
        <SignInButton style={{ marginTop: 18, padding: '10px 20px', fontSize: 14, fontWeight: 600 }}>
          Sign in to continue
        </SignInButton>
      </Wrap>
    );
  }

  if (phase === 'resolving') return <Wrap title="Reading your subscription…" detail="One moment." />;

  if (phase === 'mismatch') {
    return (
      <Wrap title="Email doesn't match the purchaser"
        detail={`This subscription was purchased by ${expectedEmail}. You're signed in as ${email}.`}>
        <div style={{ marginTop: 14, fontSize: 11, color: '#7A8899' }}>
          Sign out and sign back in with <strong>{expectedEmail}</strong>, or ask a SAARA admin
          to rebind the purchaser on the admin tenant-management page.
        </div>
      </Wrap>
    );
  }

  if (phase === 'error') {
    return (
      <Wrap title="Activation could not complete" detail={errorDetail}>
        <button className="btn-primary" style={{ marginTop: 14 }} onClick={() => setPhase('idle')}>
          Try again
        </button>
      </Wrap>
    );
  }

  if (phase === 'done') {
    return (
      <Wrap title="You're activated."
        detail="Redirecting you to your new tenant." />
    );
  }

  // phase === 'resolved'
  return (
    <Wrap title={`Activate SAARA ${resolved?.planId || ''}`}
      detail={
        <>
          Subscription <strong>{resolved?.subscriptionName || resolved?.subscriptionId}</strong> · {resolved?.quantity} {resolved?.quantity === 1 ? 'seat' : 'seats'}
          {resolved?.isFreeTrial && <> · Free trial</>}
        </>
      }>
      <div style={{ marginTop: 14, padding: 14, background: '#F8F9FA', border: '1px solid #E8ECF0', borderRadius: 8 }}>
        <Row label="Purchaser" value={resolved?.purchaser?.emailId || '—'} />
        <Row label="Offer" value={resolved?.offerId || '—'} />
        <Row label="Plan" value={resolved?.planId || '—'} />
        <Row label="Quantity" value={resolved?.quantity ?? '—'} />
        {resolved?.term && <Row label="Term" value={resolved.term} />}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: '#7A8899' }}>
        Signed in as <strong>{email}</strong>. Confirming activates your subscription
        and creates your SAARA tenant.
      </div>
      <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
        <button className="btn-primary" style={{ flex: 1, padding: '10px', fontWeight: 600 }}
          onClick={confirmActivation} disabled={phase === 'activating'}>
          {phase === 'activating' ? 'Activating…' : 'Confirm and activate'}
        </button>
        <button className="btn-secondary" style={{ flex: 1, padding: '10px' }}
          onClick={() => { window.location.href = '/'; }}>
          Cancel
        </button>
      </div>
    </Wrap>
  );
}

function Wrap({ title, detail, children }) {
  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(180deg,#0D1B2A 0%,#1B2738 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
    }}>
      <div style={{ maxWidth: 520, background: '#fff', borderRadius: 14, padding: '36px 40px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
          <img src={logo} alt="Kloudynet" style={{ width: 38, height: 38, borderRadius: 5, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A' }}>SAARA</div>
            <div style={{ fontSize: 11, color: '#7A8899' }}>Subscription activation</div>
          </div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#455A64', lineHeight: 1.6 }}>{detail}</div>
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '4px 0', fontSize: 11 }}>
      <div style={{ width: 100, color: '#7A8899' }}>{label}</div>
      <div style={{ flex: 1, color: '#0D1B2A', fontWeight: 500 }}>{value}</div>
    </div>
  );
}
